import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FieldValues, useForm } from 'react-hook-form';
import { useInfinityPagination } from './queries/useInfinityPagination';
import { GET_CONVERSATIONS } from '@/api';
import { ConversationList, NewAddMessagePayload } from '@/interfaces/inbox';
import { inboxService } from '@/api/inbox';
import { useNotification } from './useNotification';
import { formatErrorMessage } from '@/lib';
import { useEventSource } from './useEventSource';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';

interface Props {
	clientId: string;
}

export const useChatBoxLawyer = ({ clientId }: Props) => {
	const session = useSession();
	const router = useRouter();
	const queryClient = useQueryClient();
	const { id: caseId } = router.query;

	const { data: eventResponse } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/new-message/subscribe?token=${session.data?.accessToken}`,
	});

	const containerRef = React.useRef<any>(null);
	const form = useForm();
	const { toastError } = useNotification();
	const { reset } = form;

	const fetchMessages = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<ConversationList> => {
		if (!clientId || !caseId) {
			return {
				items: [],
				total: 0,
				totalItems: 0,
				totalPages: 0,
				page: 1,
				limit: 10,
			};
		}
		const response = await inboxService.getConversations({
			caseId: caseId as string,
			clientId: clientId as string,
			conversationId: '',
			page: pageParam,
			limit: 10,
		});
		return response.data;
	};

	const {
		data: conversation,
		refetch,
		isLoading: isLoadingMessages,
	} = useInfinityPagination({
		queryKey: [GET_CONVERSATIONS, clientId, caseId],
		queryFn: fetchMessages,
		enabled: !!clientId && !!caseId,
	});

	const messages = React.useMemo(() => {
		return (
			conversation?.pages.reverse().flatMap((pageItem) => pageItem.items) || []
		);
	}, [conversation]);

	const isLoading = isLoadingMessages;

	const scrollToBottom = React.useCallback(() => {
		const container = containerRef.current;
		if (container) {
			setTimeout(() => {
				container.scrollTop = container.scrollHeight;
			}, 50);
		}
	}, []);

	// Scroll to bottom on initial message load
	React.useEffect(() => {
		if (messages.length > 0 && !isLoading) {
			scrollToBottom();
		}
	}, [messages.length, isLoading, scrollToBottom]);

	const onSubmit = async (data: FieldValues): Promise<void> => {
		try {
			const newData: NewAddMessagePayload = {
				applicationId: data.applicationId,
				caseId: caseId as string,
				clientId: clientId!,
				content: data.message,
				is_bot: data.is_bot ?? false,
				action_required_id: data.action_required_id,
				message_id: data.message_id,
				document_url: data.document_url,
				document_name: data.document_name,
			};

			await inboxService.postAddMessage(newData);
			reset();
			await refetch();
			scrollToBottom();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	// Scroll to bottom on new SSE message
	React.useEffect(() => {
		if (eventResponse && clientId && caseId) {
			refetch();
			scrollToBottom();
		}
	}, [eventResponse, refetch, scrollToBottom, clientId, caseId]);

	return {
		form,
		messages,
		onSubmit,
		containerRef,
		isLoading,
	};
};
